import { bytesToHex } from '@stacks/common';
import { StacksNetwork } from '@stacks/network';
import {
  AnchorMode,
  bufferCVFromString,
  ClarityValue,
  createAssetInfo,
  FungibleConditionCode,
  listCV,
  makeStandardFungiblePostCondition,
  makeStandardNonFungiblePostCondition,
  makeStandardSTXPostCondition,
  makeUnsignedContractCall,
  makeUnsignedContractDeploy,
  makeUnsignedSTXTokenTransfer,
  noneCV,
  NonFungibleConditionCode,
  optionalCVOf,
  PostCondition,
  PostConditionMode,
  principalCV,
  uintCV,
  UnsignedContractCallOptions,
  UnsignedMultiSigContractCallOptions,
  UnsignedMultiSigContractDeployOptions,
  UnsignedMultiSigTokenTransferOptions,
} from '@stacks/transactions';
import { UTILS_DEPLOYER_ADDRESS } from 'constants/base';
import { SafeState } from 'store/safe';
import { NETWORK } from 'types/enums';

import { contractPrincipalCVFromString } from './index';

export async function generateUnsignedStxTransfer(
  nonce: number,
  recipient: string,
  amount: number,
  network: StacksNetwork,
  publicKeys: string[],
  threshold: number,
  safeAddress: string,
  fee?: number,
  memo?: string,
  anchorMode?: AnchorMode,
  notesValue?: string
) {
  const options: UnsignedMultiSigTokenTransferOptions = {
    recipient,
    memo,
    publicKeys,
    numSignatures: threshold,
    anchorMode: anchorMode ?? AnchorMode.Any,
    amount,
    network,
    nonce,
    // TODO UnsignedMultiSigTokenTransferOptions type says there is no postConditionMode for stx token transfer
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    postConditionMode: PostConditionMode.Deny,
    fee: BigInt(fee || 0),
    postConditions: [
      makeStandardSTXPostCondition(safeAddress, FungibleConditionCode.Equal, amount),
    ],
    useNonSequentialMultiSig: true,
  };

  return bytesToHex((await makeUnsignedSTXTokenTransfer(options)).serialize());
}

export const generateSendTokenTransfer = async (
  nonce: number,
  ft: string,
  amount: number,
  recipient: string,
  safe: SafeState,
  stacksNetwork: StacksNetwork,
  ref: string,
  fee?: number,
  memo?: string
) => {
  const [contractAddress, contractName] = ft.split('.');

  return await generateUnsignedContractCall(
    nonce,
    contractAddress,
    contractName,
    'transfer',
    [
      uintCV(amount),
      principalCV(safe.address),
      principalCV(recipient),
      memo ? optionalCVOf(bufferCVFromString(memo || '')) : noneCV(),
    ],
    stacksNetwork,
    safe.owners.map((o) => o.publicKey),
    safe.threshold,
    [
      makeStandardFungiblePostCondition(
        safe.address!,
        FungibleConditionCode.Equal,
        amount,
        createAssetInfo(contractAddress, contractName, ref)
      ),
    ],
    undefined,
    fee
  );
};

export const generateSendNft = async (
  nonce: number,
  nft: string,
  id: number,
  recipient: string,
  stacksNetwork: StacksNetwork,
  safe: SafeState,
  ref: string,
  fee: number
) => {
  const [contractAddress, contractName] = nft.split('.');

  return await generateUnsignedContractCall(
    nonce,
    contractAddress,
    contractName,
    'transfer',
    [uintCV(id), principalCV(safe.address), principalCV(recipient)],
    stacksNetwork,
    safe.owners.map((o) => o.publicKey),
    safe.threshold,
    [
      makeStandardNonFungiblePostCondition(
        safe.address,
        NonFungibleConditionCode.Sends,
        createAssetInfo(contractAddress, contractName, ref),
        uintCV(id)
      ),
    ],
    undefined,
    fee
  );
};

export const generateSendBatchNft = async (
  nonce: number,
  nft: string,
  ids: number[],
  recipient: string,
  network: NETWORK,
  stacksNetwork: StacksNetwork,
  safe: SafeState,
  ref: string,
  fee: number
) => {
  const [contractAddress, contractName] = nft.split('.');

  return await generateUnsignedContractCall(
    nonce,
    UTILS_DEPLOYER_ADDRESS[network],
    'batch-nft-transfer',
    'batch-nft-transfer',
    [principalCV(recipient), contractPrincipalCVFromString(nft), listCV(ids.map((i) => uintCV(i)))],
    stacksNetwork,
    safe.owners.map((o) => o.publicKey),
    safe.threshold,
    ids.map((id) =>
      makeStandardNonFungiblePostCondition(
        safe.address,
        NonFungibleConditionCode.Sends,
        createAssetInfo(contractAddress, contractName, ref),
        uintCV(id)
      )
    ),
    undefined,
    fee
  );
};

export async function generateUnsignedContractDeploy(
  options: UnsignedMultiSigContractDeployOptions
) {
  return bytesToHex((await makeUnsignedContractDeploy(options)).serialize());
}

export async function generateUnsignedContractCall(
  nonce: number,
  contractAddress: string,
  contractName: string,
  functionName: string,
  functionArgs: ClarityValue[],
  network: StacksNetwork,
  publicKeys?: string[],
  threshold?: number,
  postConditions?: PostCondition[],
  publicKey?: string,
  fee?: number,
  postConditionsMode?: PostConditionMode,
  anchorMode?: AnchorMode
) {
  if ((publicKeys?.length || 0) > 0 && publicKey) {
    throw new Error('Need to pass either multisig or singlesig parameters');
  }

  const options: Partial<UnsignedContractCallOptions | UnsignedMultiSigContractCallOptions> = {
    contractAddress,
    contractName,
    functionName,
    functionArgs,
    anchorMode: anchorMode ?? AnchorMode.Any,
    fee: BigInt(fee || 0),
    nonce,
    network,
    postConditionMode: postConditions?.length ? PostConditionMode.Deny : postConditionsMode,
    postConditions,
    useNonSequentialMultiSig: true,
  };

  if (publicKey) {
    (options as UnsignedContractCallOptions).publicKey = publicKey;
  } else if (publicKeys?.length && threshold) {
    (options as UnsignedMultiSigContractCallOptions).publicKeys = publicKeys;
    (options as UnsignedMultiSigContractCallOptions).numSignatures = threshold;
  }

  return bytesToHex(
    (
      await makeUnsignedContractCall(
        options as UnsignedContractCallOptions | UnsignedMultiSigContractCallOptions
      )
    ).serialize()
  );
}

// Might be used for testing
// export async function generateMintMockToken(
//   nonce: number,
//   recipient: string,
//   amount: number,
//   network: StacksNetwork,
//   publicKeys: string[],
//   threshold: number,
//   anchorMode?: AnchorMode
// ) {
//   const options = {
//     publicKeys,
//     numSignatures: threshold,
//     contractAddress: 'ST1PQHQKV0RJXZFY1DGX8MNSNYVE3VGZJSRTPGZGM',
//     contractName: 'ft-test',
//     functionName: 'mint',
//     functionArgs: [uintCV(amount), standardPrincipalCV(recipient)],
//     anchorMode: anchorMode ?? AnchorMode.Any,
//     nonce,
//     network,
//   };

//   return bytesToHex((await makeUnsignedContractCall(options)).serialize());
// }
